import React from 'react';
import { Edit3, Plus, Trash2, Eye, Database, ArrowRight, Loader, ChevronLeft, ChevronRight, Code, Sliders, GitBranch, Copy, Check } from 'lucide-react';
import DataFlowVisualization from './DataFlowVisualization';

const MapDataView = ({
  isDarkMode, currentView, agentDefinition, workflowResult, currentFlowIndex,
  currentNodeIndex, isAutoPilot, thinkingMessage, mapDataSubTab, variableDictExpanded,
  hoveredVarExample, generatedData, generatedSchema, previewData, editedSamples,
  previewStep, learnedPatterns, isGeneratingPrompts, isSelectingFlow, agentId,
  // Callbacks
  setCurrentNodeIndex, setMapDataSubTab, setVariableDictExpanded, setHoveredVarExample,
  setCurrentView, handleAddColumn, handleRemoveColumn, handleAddRow, handleSampleEdit,
  handleProceedToData, onGeneratePrompts,
  shouldScrollToTestData, onTestDataScrolled,
}) => {

  const testDataRef = React.useRef(null);

  // Hover-to-expand cell for long text values (matches ExecuteView pattern)
  const TruncatableCell = ({ text }) => {
    const [expanded, setExpanded] = React.useState(false);
    const [copied, setCopied] = React.useState(false);
    const handleCellCopy = (e) => {
      e.stopPropagation();
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    };
    const fadeGradient = isDarkMode
      ? 'linear-gradient(to bottom, transparent, rgba(17,24,39,0.95))'
      : 'linear-gradient(to bottom, transparent, rgba(255,255,255,0.95))';
    if (text.length <= 40) return <span>{text}</span>;
    return (
      <div
        className={`cell-truncatable${expanded ? ' cell-expanded' : ''} group/cell relative`}
        onMouseEnter={() => setExpanded(true)}
        onMouseLeave={() => { setExpanded(false); setCopied(false); }}
        onClick={() => setExpanded(prev => !prev)}
      >
        {text}
        <div className="cell-fade" style={{ background: fadeGradient }} />
        <button
          onClick={handleCellCopy}
          className={`absolute top-1 right-1 p-1 rounded opacity-0 group-hover/cell:opacity-100 transition-opacity ${
            copied
              ? (isDarkMode ? 'bg-green-600/30 text-green-400' : 'bg-green-100 text-green-600')
              : (isDarkMode ? 'bg-gray-700/80 text-gray-300 hover:text-white' : 'bg-white/90 text-gray-500 hover:text-gray-800')
          } shadow-sm`}
          title={copied ? 'Copied!' : 'Copy value'}
        >
          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
        </button>
      </div>
    );
  };

  // Auto-scroll to test data section after map-variables completes
  React.useEffect(() => {
    if (shouldScrollToTestData && testDataRef.current) {
      testDataRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      testDataRef.current.classList.add('testdata-highlight');
      const timer = setTimeout(() => {
        testDataRef.current?.classList.remove('testdata-highlight');
      }, 1500);
      onTestDataScrolled?.();
      return () => clearTimeout(timer);
    }
  }, [shouldScrollToTestData, onTestDataScrolled]);

  const renderPreviewTable = () => {
    if (!editedSamples || editedSamples.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          No preview data available
        </div>
      );
    }

    const headers = Object.keys(editedSamples[0]).filter(key => key !== '_id');

    return (
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-yellow-900/30 border-b border-yellow-700/50 p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Edit3 className="w-4 h-4 text-yellow-400" />
              <span className="text-sm font-medium text-yellow-300">Edit these samples to show your preferred data format</span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleAddColumn}
                className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-700 text-xs rounded hover:bg-blue-200 border border-blue-300"
              >
                <Plus className="w-3 h-3" />
                <span>Add Column</span>
              </button>
              <button
                onClick={handleAddRow}
                className="px-3 py-1 bg-green-100 text-green-700 text-xs rounded-full hover:bg-green-200 flex items-center space-x-1 border border-green-300"
              >
                <Plus className="w-3 h-3" />
                <span>Add Row</span>
              </button>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className={`min-w-full ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
            <thead className={`border-b ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-100 border-gray-300'}`}>
              <tr>
                <th className={`w-12 px-3 py-2 text-left text-xs font-medium uppercase tracking-wider border-r ${isDarkMode ? 'text-gray-400 border-gray-700' : 'text-gray-600 border-gray-300'}`}>
                  #
                </th>
                {headers.map((header, index) => (
                  <th
                    key={header}
                    className={`px-3 py-2 text-left text-xs font-medium uppercase tracking-wider relative group ${
                      index < headers.length - 1 ? `border-r ${isDarkMode ? 'border-gray-700' : 'border-gray-300'}` : ''
                    } ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}
                  >
                    <div className="flex items-center justify-between">
                      <span>{header.replace(/_/g, ' ')}</span>
                      <button
                        onClick={() => handleRemoveColumn(header)}
                        className={`opacity-0 group-hover:opacity-100 ml-2 p-1 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded ${isDarkMode ? 'dark-tooltip' : 'light-tooltip'}`}
                        data-tooltip={`Remove ${header} column`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
              {editedSamples.map((row, rowIndex) => (
                <tr key={rowIndex} className={isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}>
                  <td className={`w-12 px-3 py-2 text-sm border-r ${isDarkMode ? 'text-gray-400 border-gray-700 bg-gray-900' : 'text-gray-500 border-gray-300 bg-gray-100'}`}>
                    {rowIndex + 1}
                  </td>
                  {headers.map((header, colIndex) => (
                    <td
                      key={header}
                      className={`px-2 py-2 ${
                        colIndex < headers.length - 1 ? `border-r ${isDarkMode ? 'border-gray-700' : 'border-gray-300'}` : ''
                      }`}
                    >
                      <textarea
                        value={typeof row[header] === 'object' ? JSON.stringify(row[header]) : String(row[header] || '')}
                        onChange={(e) => {
                          let newValue = e.target.value;
                          try {
                            if (newValue.startsWith('[') || newValue.startsWith('{')) {
                              newValue = JSON.parse(newValue);
                            }
                          } catch {
                          }
                          handleSampleEdit(rowIndex, header, newValue);
                        }}
                        rows={typeof row[header] === 'object' ? 3 : 1}
                        className={`w-full px-2 py-1 text-sm border border-transparent rounded hover:border-blue-500/50 focus:border-blue-500 focus:outline-none resize-none ${isDarkMode ? 'bg-gray-800 text-gray-200' : 'bg-gray-50 text-gray-700'}`}
                        style={{ minWidth: '120px' }}
                        placeholder={`Enter ${header.replace(/_/g, ' ')}`}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderDataTable = () => {
    if (!generatedData?.rows || generatedData.rows.length === 0) {
      return (
        <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          No data available
        </div>
      );
    }

    const headers = Object.keys(generatedData.rows[0]).filter(key => key !== '_id');

    return (
      <div className={`rounded-lg overflow-hidden ${isDarkMode ? 'border border-gray-700' : 'border border-gray-200'}`}>
        <div className="overflow-x-auto">
          <table className={`min-w-full ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
            <thead className={`border-b ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-100 border-gray-300'}`}>
              <tr>
                <th className={`w-12 px-3 py-2 text-left text-xs font-medium uppercase tracking-wider border-r ${isDarkMode ? 'text-gray-400 border-gray-700' : 'text-gray-600 border-gray-300'}`}>
                  #
                </th>
                {headers.map((header, index) => (
                  <th
                    key={header}
                    className={`px-3 py-2 text-left text-xs font-medium uppercase tracking-wider ${
                      index < headers.length - 1 ? `border-r ${isDarkMode ? 'border-gray-700' : 'border-gray-300'}` : ''
                    } ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}
                  >
                    {header.replace(/_/g, ' ')}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
              {generatedData.rows.map((row, rowIndex) => (
                <tr key={rowIndex} className={isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}>
                  <td className={`w-12 px-3 py-2 text-sm border-r ${isDarkMode ? 'text-gray-400 border-gray-700 bg-gray-900' : 'text-gray-500 border-gray-300 bg-gray-100'}`}>
                    {rowIndex + 1}
                  </td>
                  {headers.map((header, colIndex) => (
                    <td
                      key={header}
                      className={`px-3 py-2 text-sm ${
                        colIndex < headers.length - 1 ? `border-r ${isDarkMode ? 'border-gray-700' : 'border-gray-300'}` : ''
                      } ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
                    >
                      <div className="max-w-xs truncate" title={String(row[header])}>
                        {typeof row[header] === 'object'
                          ? JSON.stringify(row[header])
                          : String(row[header])
                        }
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderLearnedPatterns = () => {
    if (!learnedPatterns || Object.keys(learnedPatterns).length === 0) {
      return null;
    }

    return (
      <div className="space-y-4">
        <h4 className={`font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>Learned Patterns</h4>
        <div className="grid grid-cols-1 gap-3">
          {Object.entries(learnedPatterns).map(([field, pattern]) => (
            <div key={field} className={`rounded-lg p-3 ${isDarkMode ? 'bg-green-900/30 border border-green-700' : 'bg-green-100 border border-green-300'}`}>
              <div className={`font-medium mb-2 ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>{field.replace(/_/g, ' ')}</div>
              <div className={`text-sm space-y-1 ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
                {pattern.type === 'string_pattern' && (
                  <>
                    {pattern.formats && pattern.formats.length > 0 && (
                      <div><span className="font-medium">Formats:</span> {pattern.formats.join(', ')}</div>
                    )}
                    {pattern.examples && (
                      <div><span className="font-medium">Examples:</span> {pattern.examples.join(', ')}</div>
                    )}
                    {pattern.length_range && (
                      <div><span className="font-medium">Length:</span> {pattern.length_range[0]} - {pattern.length_range[1]} characters</div>
                    )}
                  </>
                )}
                {pattern.type === 'numeric_pattern' && (
                  <>
                    {pattern.range && (
                      <div><span className="font-medium">Range:</span> {pattern.range[0]} - {pattern.range[1]}</div>
                    )}
                    {pattern.examples && (
                      <div><span className="font-medium">Examples:</span> {pattern.examples.join(', ')}</div>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderPreviewSteps = () => {
    const steps = [
      { step: 1, label: 'Preview', icon: Eye, active: previewStep >= 1 },
      { step: 2, label: 'Edit', icon: Edit3, active: previewStep >= 2 },
      { step: 3, label: 'Generate', icon: Database, active: previewStep >= 3 }
    ];

    return (
      <div className="flex items-center space-x-4 mb-6">
        {steps.map((stepInfo, index) => {
          const Icon = stepInfo.icon;
          return (
            <div key={stepInfo.step} className="flex items-center">
              <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm ${
                stepInfo.active
                  ? isDarkMode ? 'bg-blue-900/40 text-blue-400 border border-blue-600' : 'bg-blue-100 text-blue-700 border border-blue-300'
                  : isDarkMode ? 'bg-gray-800 text-gray-400 border border-gray-600' : 'bg-gray-100 text-gray-500 border border-gray-300'
              }`}>
                <Icon className="w-4 h-4" />
                <span>{stepInfo.label}</span>
              </div>
              {index < steps.length - 1 && (
                <ArrowRight className={`w-4 h-4 mx-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`} />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      {currentView === 'data-preview' && previewData ? (
        <div className="space-y-6">
          {renderPreviewSteps()}

          <div className="bg-orange-900/30 border border-orange-700/50 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-orange-300 mb-2">
              {previewStep === 1 && 'Preview Data Samples'}
              {previewStep === 2 && 'Patterns Learned!'}
              {previewStep === 3 && 'Data Generated!'}
            </h3>
            <p className="text-orange-200 text-sm">
              {previewStep === 1 && 'Edit these samples to show your preferred data format. I\'ll learn your patterns and generate realistic data that follows them.'}
              {previewStep === 2 && 'I\'ve learned your data patterns! Now I can generate realistic data that follows your preferred format.'}
              {previewStep === 3 && 'Your realistic test data has been generated following your patterns!'}
            </p>
          </div>

          {previewStep >= 1 && (
            <div className="space-y-4">
              <h4 className={`font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>Sample Data Preview</h4>
              {renderPreviewTable()}
            </div>
          )}

          {previewStep >= 2 && learnedPatterns && (
            <div className="space-y-4">
              {renderLearnedPatterns()}
            </div>
          )}
        </div>
      ) : currentView === 'map-data' ? (
        <div className="space-y-6">
          {/* Auto-pilot skeleton for mapping variables */}
          {isAutoPilot && thinkingMessage && !agentDefinition?.nodes?.some(n => n.field_sources?.length > 0) ? (
            <div className="space-y-6 py-4">
              {/* Animated header skeleton */}
              <div className={`rounded-lg p-5 ${isDarkMode ? 'bg-cyan-900/20 border border-cyan-800/30' : 'bg-cyan-50/80 border border-cyan-200'}`}>
                <div className="flex items-center space-x-3 mb-3">
                  <Loader className={`w-5 h-5 animate-spin ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-cyan-300' : 'text-cyan-700'}`}>
                    {thinkingMessage || 'Mapping variables...'}
                  </span>
                </div>
                <div className={`h-2 rounded-full w-3/4 mapdata-skeleton-bar ${isDarkMode ? 'bg-cyan-800/50' : 'bg-cyan-200'}`} />
              </div>

              {/* Node connection skeletons */}
              {[0, 1, 2].map(i => (
                <div key={i} className={`rounded-lg p-5 ${isDarkMode ? 'bg-cyan-900/20 border border-cyan-800/30' : 'bg-cyan-50/60 border border-cyan-200'}`}>
                  <div className="flex items-center space-x-4">
                    {/* Source node */}
                    <div className="flex-1">
                      <div className={`h-3 rounded w-24 mb-2 mapdata-skeleton-bar ${isDarkMode ? 'bg-cyan-700/50' : 'bg-cyan-300/70'}`} style={{ animationDelay: `${i * 200}ms` }} />
                      <div className="space-y-1.5">
                        {[0, 1, 2].map(j => (
                          <div key={j} className={`h-2 rounded mapdata-skeleton-bar ${isDarkMode ? 'bg-cyan-700/35' : 'bg-cyan-200/80'}`}
                            style={{ width: `${60 + Math.random() * 30}%`, animationDelay: `${(i * 3 + j) * 150}ms` }} />
                        ))}
                      </div>
                    </div>
                    {/* Arrow */}
                    <div className={`flex flex-col items-center space-y-1 ${isDarkMode ? 'text-cyan-500' : 'text-cyan-500'}`}>
                      <ArrowRight className="w-5 h-5" />
                      <div className={`h-1.5 w-8 rounded mapdata-skeleton-bar ${isDarkMode ? 'bg-cyan-600/40' : 'bg-cyan-300/80'}`}
                        style={{ animationDelay: `${i * 300}ms` }} />
                    </div>
                    {/* Target node */}
                    <div className="flex-1">
                      <div className={`h-3 rounded w-28 mb-2 mapdata-skeleton-bar ${isDarkMode ? 'bg-cyan-700/50' : 'bg-cyan-300/70'}`} style={{ animationDelay: `${i * 200 + 100}ms` }} />
                      <div className="space-y-1.5">
                        {[0, 1].map(j => (
                          <div key={j} className={`h-2 rounded mapdata-skeleton-bar ${isDarkMode ? 'bg-cyan-700/35' : 'bg-cyan-200/80'}`}
                            style={{ width: `${50 + Math.random() * 40}%`, animationDelay: `${(i * 3 + j + 3) * 150}ms` }} />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : agentDefinition?.nodes?.some(n => n.field_sources?.length > 0) ? (
            <>
              {/* Header - Variables Mapped */}
              <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-cyan-900/30 border border-cyan-700/50' : 'bg-cyan-50 border border-cyan-200'}`}>
                <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-cyan-300' : 'text-cyan-700'}`}>
                  Generated Test Data
                </h3>
                <p className={`text-sm ${isDarkMode ? 'text-cyan-200' : 'text-cyan-600'}`}>
                  Data flow connections have been established between your agent's nodes.
                </p>
              </div>

              {/* Data Flow Visualization - Always visible */}
              <div className={`rounded-lg p-6 ${isDarkMode ? 'bg-gray-800/50 border border-gray-700' : 'bg-white border border-gray-200'}`}>
                <DataFlowVisualization
                  nodes={workflowResult?.flows?.[currentFlowIndex]?.workflow_steps?.map(step => ({
                    nodeId: step.node?.nodeId || step.node?.id || step.position?.toString(),
                    alias: step.node?.display_name || step.node?.alias || `Node ${step.position}`,
                    description: step.node?.description || step.node?.business_function || ''
                  })) || []}
                  workflowSteps={workflowResult?.flows?.[currentFlowIndex]?.workflow_steps || []}
                  isDarkMode={isDarkMode}
                  agentDefinition={agentDefinition}
                  currentNodeIndex={currentNodeIndex}
                  onNodeClick={setCurrentNodeIndex}
                />
              </div>

              {/* Sub-tabs: Variable Dictionary | Test Data */}
              <div className={`flex space-x-1 p-1 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <button
                  onClick={() => setMapDataSubTab('flow')}
                  className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    mapDataSubTab === 'flow'
                      ? isDarkMode
                        ? 'bg-gray-700 text-blue-400'
                        : 'bg-white text-blue-600 shadow-sm'
                      : isDarkMode
                        ? 'text-gray-400 hover:text-gray-200'
                        : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  Variable Dictionary
                </button>
                <button
                  onClick={() => setMapDataSubTab('test-data')}
                  className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    mapDataSubTab === 'test-data'
                      ? isDarkMode
                        ? 'bg-gray-700 text-blue-400'
                        : 'bg-white text-blue-600 shadow-sm'
                      : isDarkMode
                        ? 'text-gray-400 hover:text-gray-200'
                        : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  Test Data
                </button>
              </div>

              {/* Variable Dictionary View */}
              {mapDataSubTab === 'flow' && (
                <>
                  {/* Variable Dictionary Summary */}
              {agentDefinition?.variable_dict && Object.keys(agentDefinition.variable_dict).length > 0 && (
                <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-gray-800/50 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className={`font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                      Variable Dictionary ({Object.keys(agentDefinition.variable_dict).length} variables)
                    </h4>
                    {Object.keys(agentDefinition.variable_dict).length > 6 && (
                      <button
                        onClick={() => setVariableDictExpanded(!variableDictExpanded)}
                        className={`text-xs px-2 py-1 rounded transition-colors ${
                          isDarkMode
                            ? 'bg-gray-700 hover:bg-gray-600 text-blue-400'
                            : 'bg-gray-200 hover:bg-gray-300 text-blue-600'
                        }`}
                      >
                        {variableDictExpanded ? 'Show Less' : `Show All ${Object.keys(agentDefinition.variable_dict).length}`}
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {Object.entries(agentDefinition.variable_dict)
                      .slice(0, variableDictExpanded ? undefined : 6)
                      .map(([varName, varInfo]) => {
                        const exampleValue = varInfo.values && varInfo.values.length > 0
                          ? (typeof varInfo.values[0] === 'object' ? JSON.stringify(varInfo.values[0], null, 2) : String(varInfo.values[0]))
                          : null;
                        const truncatedExample = exampleValue
                          ? (exampleValue.length > 50 ? exampleValue.slice(0, 50) + '...' : exampleValue)
                          : null;
                        return (
                          <div
                            key={varName}
                            className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700/50' : 'bg-white border border-gray-200'}`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className={`font-medium text-sm ${isDarkMode ? 'text-cyan-300' : 'text-cyan-700'}`}>
                                {varName}
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                                {varInfo.type || 'string'}
                              </span>
                            </div>
                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              Source: {varInfo.source === -1 ? 'Agent Input' : `Node ${varInfo.source + 1}`}
                            </div>
                            {truncatedExample && (
                              <div
                                className="relative"
                                onMouseEnter={() => setHoveredVarExample(varName)}
                                onMouseLeave={() => setHoveredVarExample(null)}
                              >
                                <div
                                  className={`text-xs mt-1 truncate cursor-help ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}
                                >
                                  Example: "{truncatedExample}"
                                </div>
                                {hoveredVarExample === varName && exampleValue.length > 50 && (
                                  <div
                                    className={`absolute z-50 left-0 bottom-full mb-2 p-3 rounded-lg shadow-xl max-w-md max-h-64 overflow-auto text-xs whitespace-pre-wrap ${
                                      isDarkMode
                                        ? 'bg-gray-900 border border-gray-600 text-gray-200'
                                        : 'bg-white border border-gray-300 text-gray-700'
                                    }`}
                                    style={{ minWidth: '200px' }}
                                  >
                                    {exampleValue}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
                </>
              )}

              {/* Test Data View */}
              {mapDataSubTab === 'test-data' && (() => {
                // Get the actual schema fields (handle nested {schema: {...}} structure)
                const agentInputFields = agentDefinition?.agent_input?.schema || agentDefinition?.agent_input || {};
                const agentOutputFields = agentDefinition?.agent_output?.schema || agentDefinition?.agent_output || {};
                const nodeCount = agentDefinition?.nodes?.length || 0;
                // Total items: Agent Input (-1) + Nodes (0 to nodeCount-1) + Agent Output (nodeCount)
                const totalItems = nodeCount + 2;
                // Map currentNodeIndex to carousel position: -1 -> 0, 0 -> 1, ..., nodeCount -> nodeCount+1
                const carouselIndex = currentNodeIndex + 1;

                // Get current item label
                const getCurrentLabel = () => {
                  if (currentNodeIndex === -1) return 'Agent Input';
                  if (currentNodeIndex === nodeCount) return 'Agent Output';
                  return agentDefinition?.nodes?.[currentNodeIndex]?.alias || `Node ${currentNodeIndex + 1}`;
                };

                return (
                  <div className="space-y-4">
                    {/* Carousel Navigation */}
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => setCurrentNodeIndex(prev => Math.max(-1, prev - 1))}
                        disabled={currentNodeIndex === -1}
                        className={`p-2 rounded-full transition-colors ${
                          currentNodeIndex === -1
                            ? isDarkMode ? 'text-gray-600 cursor-not-allowed' : 'text-gray-400 cursor-not-allowed'
                            : isDarkMode ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-200' : 'text-gray-600 hover:bg-gray-200 hover:text-gray-800'
                        }`}
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>

                      {/* Dot indicators */}
                      <div className="flex items-center space-x-2">
                        {/* Agent Input dot - Cyan */}
                        <button
                          onClick={() => setCurrentNodeIndex(-1)}
                          className={`w-2.5 h-2.5 rounded-full transition-colors ${
                            currentNodeIndex === -1 ? 'bg-cyan-500' : isDarkMode ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-400 hover:bg-gray-500'
                          }`}
                          title="Agent Input"
                        />
                        {/* Node dots - Rose (integration), Green (agent output), or Blue (regular) */}
                        {agentDefinition?.nodes?.map((_, index) => {
                          const workflowSteps = workflowResult?.flows?.[currentFlowIndex]?.workflow_steps || [];
                          const stepNodeDetails = workflowSteps[index]?.node?.node_details || {};
                          const hasIntegration = stepNodeDetails.requires_integration || stepNodeDetails.integration_hint;
                          const hasAgentOutput = stepNodeDetails.requires_agent_output || stepNodeDetails.agent_output_hint;
                          const dotColor = hasIntegration ? 'bg-rose-500' : hasAgentOutput ? 'bg-green-500' : 'bg-blue-500';
                          return (
                            <button
                              key={index}
                              onClick={() => setCurrentNodeIndex(index)}
                              className={`w-2.5 h-2.5 rounded-full transition-colors ${
                                index === currentNodeIndex ? dotColor : isDarkMode ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-400 hover:bg-gray-500'
                              }`}
                              title={agentDefinition?.nodes?.[index]?.alias || `Node ${index + 1}`}
                            />
                          );
                        })}
                        {/* Agent Output dot - Green */}
                        <button
                          onClick={() => setCurrentNodeIndex(nodeCount)}
                          className={`w-2.5 h-2.5 rounded-full transition-colors ${
                            currentNodeIndex === nodeCount ? 'bg-green-500' : isDarkMode ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-400 hover:bg-gray-500'
                          }`}
                          title="Agent Output"
                        />
                      </div>

                      <button
                        onClick={() => setCurrentNodeIndex(prev => Math.min(nodeCount, prev + 1))}
                        disabled={currentNodeIndex === nodeCount}
                        className={`p-2 rounded-full transition-colors ${
                          currentNodeIndex === nodeCount
                            ? isDarkMode ? 'text-gray-600 cursor-not-allowed' : 'text-gray-400 cursor-not-allowed'
                            : isDarkMode ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-200' : 'text-gray-600 hover:bg-gray-200 hover:text-gray-800'
                        }`}
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Current item label */}
                    <div className="text-center">
                      <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {carouselIndex + 1} of {totalItems}:
                      </span>
                      <span className={`ml-2 font-medium ${
                        (() => {
                          // Agent Input - Cyan
                          if (currentNodeIndex === -1) {
                            return isDarkMode ? 'text-cyan-400' : 'text-cyan-600';
                          }
                          // Agent Output - Green
                          if (currentNodeIndex === nodeCount) {
                            return isDarkMode ? 'text-green-400' : 'text-green-600';
                          }
                          // Nodes - check for integration (Rose), agent output (Green), or regular (Blue)
                          const workflowSteps = workflowResult?.flows?.[currentFlowIndex]?.workflow_steps || [];
                          const stepNodeDetails = workflowSteps[currentNodeIndex]?.node?.node_details || {};
                          const hasIntegration = stepNodeDetails.requires_integration || stepNodeDetails.integration_hint;
                          const hasAgentOutput = stepNodeDetails.requires_agent_output || stepNodeDetails.agent_output_hint;
                          if (hasIntegration) {
                            return isDarkMode ? 'text-rose-400' : 'text-rose-600';
                          }
                          if (hasAgentOutput) {
                            return isDarkMode ? 'text-green-400' : 'text-green-600';
                          }
                          return isDarkMode ? 'text-blue-400' : 'text-blue-600';
                        })()
                      }`}>
                        {getCurrentLabel()}
                      </span>
                    </div>

                    {/* Selected Node Table */}
                    <div ref={testDataRef} className={`rounded-lg overflow-hidden ${isDarkMode ? 'bg-gray-800/50 border border-gray-700' : 'bg-white border border-gray-200'}`}>
                      {/* Agent Input Table */}
                      {currentNodeIndex === -1 && (
                        <div className="p-4">
                          <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-cyan-300' : 'text-cyan-700'}`}>
                            Agent Input - Test Data
                          </h4>
                          <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            Input data that starts the workflow execution
                          </p>
                          <div className="overflow-x-auto">
                            <table className={`w-full text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              <thead>
                                <tr className={isDarkMode ? 'border-b border-gray-700' : 'border-b border-gray-200'}>
                                  <th className={`px-3 py-2 text-left font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Field</th>
                                  <th className={`px-3 py-2 text-left font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Type</th>
                                  <th className={`px-3 py-2 text-left font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Example 1</th>
                                  <th className={`px-3 py-2 text-left font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Example 2</th>
                                  <th className={`px-3 py-2 text-left font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Example 3</th>
                                </tr>
                              </thead>
                              <tbody>
                                {Object.entries(agentInputFields).map(([fieldName, fieldType]) => {
                                  const typeStr = typeof fieldType === 'object' ? JSON.stringify(fieldType) : (fieldType || 'string');
                                  const varInfo = agentDefinition?.variable_dict?.[fieldName];
                                  return (
                                    <tr key={fieldName} className={isDarkMode ? 'border-b border-gray-700/50' : 'border-b border-gray-100'}>
                                      <td className={`px-3 py-2 font-medium ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>{fieldName}</td>
                                      <td className={`px-3 py-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{typeStr}</td>
                                      {[0, 1, 2].map(idx => {
                                        const value = varInfo?.values?.[idx];
                                        const displayValue = value !== undefined
                                          ? (typeof value === 'object' ? JSON.stringify(value) : String(value))
                                          : '-';
                                        return (
                                          <td key={idx} className="px-3 py-2">
                                            <TruncatableCell text={displayValue} />
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Node Tables */}
                      {agentDefinition?.nodes?.map((node, nodeIdx) => {
                        if (currentNodeIndex !== nodeIdx) return null;
                        // Handle nested schema structure for templates (backend uses node_input/node_output)
                        const inputFields = Object.entries(node.node_input?.schema || node.node_input || node.requestTemplate?.schema || node.requestTemplate || {});
                        const outputFields = Object.entries(node.node_output?.schema || node.node_output || node.responseTemplate?.schema || node.responseTemplate || {});
                        // Check node type for color matching - use workflowSteps for consistency with flow diagram
                        // Colors: Integration=Rose, AgentOutput=Green, Regular=Blue
                        const workflowSteps = workflowResult?.flows?.[currentFlowIndex]?.workflow_steps || [];
                        const stepNodeDetails = workflowSteps[nodeIdx]?.node?.node_details || {};
                        const hasIntegration = stepNodeDetails.requires_integration || stepNodeDetails.integration_hint || node.integration_hint || node.integrationHint;
                        const hasAgentOutput = stepNodeDetails.requires_agent_output || stepNodeDetails.agent_output_hint;
                        const nodeColor = hasIntegration
                          ? (isDarkMode ? 'text-rose-300' : 'text-rose-700')
                          : hasAgentOutput
                            ? (isDarkMode ? 'text-green-300' : 'text-green-700')
                            : (isDarkMode ? 'text-blue-300' : 'text-blue-700');

                        return (
                          <div key={nodeIdx} className="p-4 space-y-6">
                            <h4 className={`font-semibold ${nodeColor}`}>
                              {node.alias || `Node ${nodeIdx + 1}`} - Test Data
                            </h4>

                            {/* Input Fields */}
                            {inputFields.length > 0 && (
                              <div>
                                <h5 className={`text-xs font-semibold mb-2 uppercase ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  Input Fields
                                </h5>
                                <div className="overflow-x-auto">
                                  <table className={`w-full text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    <thead>
                                      <tr className={isDarkMode ? 'border-b border-gray-700' : 'border-b border-gray-200'}>
                                        <th className={`px-3 py-2 text-left font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Field</th>
                                        <th className={`px-3 py-2 text-left font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Type</th>
                                        <th className={`px-3 py-2 text-left font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Example 1</th>
                                        <th className={`px-3 py-2 text-left font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Example 2</th>
                                        <th className={`px-3 py-2 text-left font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Example 3</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {inputFields.map(([fieldName, fieldType]) => {
                                        const typeStr = typeof fieldType === 'object' ? JSON.stringify(fieldType) : (fieldType || 'string');
                                        const varInfo = agentDefinition?.variable_dict?.[fieldName];
                                        return (
                                          <tr key={fieldName} className={isDarkMode ? 'border-b border-gray-700/50' : 'border-b border-gray-100'}>
                                            <td className={`px-3 py-2 font-medium ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>{fieldName}</td>
                                            <td className={`px-3 py-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{typeStr}</td>
                                            {[0, 1, 2].map(idx => {
                                              const value = varInfo?.values?.[idx];
                                              const displayValue = value !== undefined
                                                ? (typeof value === 'object' ? JSON.stringify(value) : String(value))
                                                : '-';
                                              return (
                                                <td key={idx} className="px-3 py-2">
                                                  <TruncatableCell text={displayValue} />
                                                </td>
                                              );
                                            })}
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}

                            {/* Output Fields */}
                            {outputFields.length > 0 && (
                              <div>
                                <h5 className={`text-xs font-semibold mb-2 uppercase ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  Output Fields
                                </h5>
                                <div className="overflow-x-auto">
                                  <table className={`w-full text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    <thead>
                                      <tr className={isDarkMode ? 'border-b border-gray-700' : 'border-b border-gray-200'}>
                                        <th className={`px-3 py-2 text-left font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Field</th>
                                        <th className={`px-3 py-2 text-left font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Type</th>
                                        <th className={`px-3 py-2 text-left font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} colSpan={3}>Value</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {outputFields.map(([fieldName, fieldType]) => {
                                        const typeStr = typeof fieldType === 'object' ? JSON.stringify(fieldType) : (fieldType || 'string');
                                        return (
                                          <tr key={fieldName} className={isDarkMode ? 'border-b border-gray-700/50' : 'border-b border-gray-100'}>
                                            <td className={`px-3 py-2 font-medium ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>{fieldName}</td>
                                            <td className={`px-3 py-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{typeStr}</td>
                                            <td className={`px-3 py-2 italic ${isDarkMode ? 'text-amber-400/70' : 'text-amber-600/70'}`} colSpan={3}>
                                              To be generated during execution
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}

                            {inputFields.length === 0 && outputFields.length === 0 && (
                              <p className={`text-center py-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                No input/output fields defined for this node
                              </p>
                            )}
                          </div>
                        );
                      })}

                      {/* Agent Output Table */}
                      {currentNodeIndex === nodeCount && (
                        <div className="p-4">
                          <h4 className={`font-semibold mb-1 ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>
                            Agent Output
                          </h4>
                          <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            Final output returned by the workflow
                          </p>
                          <div className="overflow-x-auto">
                            <table className={`w-full text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              <thead>
                                <tr className={isDarkMode ? 'border-b border-gray-700' : 'border-b border-gray-200'}>
                                  <th className={`px-3 py-2 text-left font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Field</th>
                                  <th className={`px-3 py-2 text-left font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Type</th>
                                  <th className={`px-3 py-2 text-left font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Value</th>
                                </tr>
                              </thead>
                              <tbody>
                                {Object.entries(agentOutputFields).map(([fieldName, fieldType]) => {
                                  const typeStr = typeof fieldType === 'object' ? JSON.stringify(fieldType) : (fieldType || 'string');
                                  return (
                                    <tr key={fieldName} className={isDarkMode ? 'border-b border-gray-700/50' : 'border-b border-gray-100'}>
                                      <td className={`px-3 py-2 font-medium ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>{fieldName}</td>
                                      <td className={`px-3 py-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{typeStr}</td>
                                      <td className={`px-3 py-2 italic ${isDarkMode ? 'text-amber-400/70' : 'text-amber-600/70'}`}>
                                        To be generated during execution
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Generate Prompts CTA */}
              <div className={`rounded-lg p-6 ${isDarkMode ? 'bg-[#0077A8]/20 border border-[#0077A8]/50' : 'bg-[#0077A8]/10 border border-[#0077A8]/30'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className={`text-lg font-semibold mb-1 ${isDarkMode ? 'text-cyan-300' : 'text-[#0077A8]'}`}>
                      Ready to Generate Prompts
                    </h3>
                    <p className={`text-sm ${isDarkMode ? 'text-cyan-200/80' : 'text-[#0077A8]/80'}`}>
                      Variables are mapped. Generate AI prompts for each node to make your agent functional.
                    </p>
                  </div>
                  <button
                    onClick={onGeneratePrompts}
                    disabled={isGeneratingPrompts}
                    className={`px-6 py-3 rounded-lg font-medium flex items-center space-x-2 transition-all ${
                      isGeneratingPrompts
                        ? 'bg-gray-500 cursor-not-allowed text-white'
                        : 'bg-[#0077A8] hover:bg-[#006090] text-white shadow-lg hover:shadow-xl'
                    }`}
                  >
                    {isGeneratingPrompts ? (
                      <>
                        <Loader className="w-5 h-5 animate-spin" />
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <Code className="w-5 h-5" />
                        <span>Generate Prompts</span>
                        <ChevronRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </>
          ) : agentId ? (
            /* Agent exists but variables not mapped yet - show Map Data button */
            <div className={`flex flex-col items-center justify-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <Sliders className={`w-16 h-16 mb-4 ${isDarkMode ? 'text-cyan-500' : 'text-cyan-600'}`} />
              <h3 className="text-lg font-medium mb-2">Map Variable Connections</h3>
              <p className="text-center mb-6 max-w-md">
                Establish data flow connections between your agent's nodes. This determines how data passes from one step to the next.
              </p>
              <button
                onClick={handleProceedToData}
                disabled={isSelectingFlow}
                className="px-6 py-3 bg-[#0077A8] text-white rounded-lg hover:bg-[#006090] flex items-center space-x-2 text-lg"
              >
                {isSelectingFlow ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    <span>Mapping Variables...</span>
                  </>
                ) : (
                  <>
                    <Sliders className="w-5 h-5" />
                    <span>Map Data</span>
                  </>
                )}
              </button>
            </div>
          ) : (
            /* No agent yet - prompt to select flow first */
            <div className={`flex flex-col items-center justify-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <GitBranch className={`w-16 h-16 mb-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
              <h3 className="text-lg font-medium mb-2">Select a Flow First</h3>
              <p className="text-center">
                Go to the Plan tab and select a flow to create your agent before mapping variables.
              </p>
            </div>
          )}
        </div>
      ) : currentView === 'data' && generatedData ? (
        <div className="space-y-6">
          <div className="bg-teal-900/30 border border-teal-700/50 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-teal-300 mb-2">Realistic Test Data Generated!</h3>
            <p className="text-teal-200 text-sm mb-3">
              Generated {generatedData.numRows} rows of realistic test data following your preferred patterns.
              This data is ready for testing your AI Agent.
            </p>
            <div className="text-sm text-teal-200">
              <strong>Generated:</strong> {new Date(generatedData.generatedAt).toLocaleString()}
            </div>
          </div>

          {generatedSchema && (
            <div className="space-y-4">
              <h4 className={`font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>Schema Information</h4>
              <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-gray-800/50 border border-gray-700' : 'bg-gray-100/50 border border-gray-300'}`}>
                <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{generatedSchema.description}</p>
                <div className="space-y-2">
                  <h5 className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Data Flow:</h5>
                  <ul className={`text-sm space-y-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {generatedSchema.dataFlowExplanation.map((step, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <span className="font-medium">{index + 1}.</span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <h4 className={`font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>Generated Data</h4>
            {renderDataTable()}
          </div>
        </div>
      ) : null}
    </>
  );
};

export default MapDataView;
